import { useState, useEffect } from 'react';
import { ref, get, update, set } from 'firebase/database';
import { HiOutlineViewGrid, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { db } from '../firebase';
import WatchlistContent from '../components/WatchlistContent';
import { useAdminToast } from '../admin/AdminLayout';
import {
  ANALYZE_WATCHLISTS_REF,
  WATCHLIST_EXCHANGES_REF,
  WATCHLIST_DISABLED,
  EXCHANGE_OPTIONS,
  WATCHLIST_REF,
  ANALYSIS_REF,
  REPORT_PUBLISH_REF,
  REPORT_WORKSPACE_REF,
} from '../admin/adminConstants';

function AdminWatchlists({ isAdmin }) {
  const { showToast } = useAdminToast();
  const [watchlists, setWatchlists] = useState(null);
  const [watchlistExchanges, setWatchlistExchanges] = useState({});
  const [watchlistByGroup, setWatchlistByGroup] = useState(null);
  const [watchlistsLoading, setWatchlistsLoading] = useState(true);
  const [watchlistExchangesLoading, setWatchlistExchangesLoading] = useState(true);
  const [watchlistsError, setWatchlistsError] = useState(null);
  const [savingWatchlist, setSavingWatchlist] = useState(null);
  const [showCreateWatchlist, setShowCreateWatchlist] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [newWatchlistExchange, setNewWatchlistExchange] = useState('US');
  const [creatingWatchlist, setCreatingWatchlist] = useState(false);
  const [showEditWatchlist, setShowEditWatchlist] = useState(false);
  const [editingWatchlistCode, setEditingWatchlistCode] = useState(null);
  const [editWatchlistName, setEditWatchlistName] = useState('');
  const [editWatchlistExchange, setEditWatchlistExchange] = useState('');
  const [savingEditWatchlist, setSavingEditWatchlist] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
        setWatchlistsLoading(true);
        setWatchlistExchangesLoading(true);
        setWatchlistsError(null);
        try {
        const [countrySnap, watchlistExchangesSnap, watchlistSnap] = await Promise.all([
          get(ref(db, ANALYZE_WATCHLISTS_REF)),
          get(ref(db, WATCHLIST_EXCHANGES_REF)),
          get(ref(db, WATCHLIST_REF)),
        ]);
        if (cancelled) return;
        const cd = countrySnap.val();
        setWatchlists(typeof cd === 'object' && cd !== null ? cd : {});
        const ge = watchlistExchangesSnap.val();
        setWatchlistExchanges(typeof ge === 'object' && ge !== null ? ge : {});
        const wl = watchlistSnap.val();
        setWatchlistByGroup(typeof wl === 'object' && wl !== null ? wl : {});
      } catch (err) {
        if (!cancelled) setWatchlistsError(err.message || 'Failed to load watchlists');
      } finally {
        if (!cancelled) {
          setWatchlistsLoading(false);
          setWatchlistExchangesLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleWatchlistToggle = async (code) => {
    const isEnabled = watchlists[code] != null && watchlists[code] !== '' && watchlists[code] !== WATCHLIST_DISABLED;
    const nextValue = isEnabled ? WATCHLIST_DISABLED : (watchlistExchanges[code] || 'US');
    setWatchlistsError(null);
    setSavingWatchlist(code);
    try {
      await update(ref(db, ANALYZE_WATCHLISTS_REF), { [code]: nextValue });
      setWatchlists((prev) => ({ ...prev, [code]: nextValue }));
      showToast('Watchlist updated');
    } catch (err) {
      setWatchlistsError(err.message || 'Failed to update watchlist');
    } finally {
      setSavingWatchlist(null);
    }
  };

  const handleCreateWatchlist = async (e) => {
    e?.preventDefault?.();
    const name = newWatchlistName.trim().toUpperCase();
    if (!name) return;
    if (!/^[A-Z0-9_-]+$/.test(name)) {
      setWatchlistsError('Watchlist name must contain only letters, numbers, underscore, or hyphen');
      return;
    }
    if (watchlists && watchlists[name] !== undefined) {
      setWatchlistsError('Watchlist already exists');
      return;
    }
    setCreatingWatchlist(true);
    setWatchlistsError(null);
    try {
      await update(ref(db, WATCHLIST_REF), { [name]: {} });
      await update(ref(db, ANALYZE_WATCHLISTS_REF), { [name]: WATCHLIST_DISABLED });
      await update(ref(db, WATCHLIST_EXCHANGES_REF), { [name]: newWatchlistExchange });
      setWatchlists((prev) => ({ ...prev, [name]: WATCHLIST_DISABLED }));
      setWatchlistExchanges((prev) => ({ ...prev, [name]: newWatchlistExchange }));
      setWatchlistByGroup((prev) => ({
        ...(prev || {}),
        [name]: {},
      }));
      setNewWatchlistName('');
      setNewWatchlistExchange('US');
      setShowCreateWatchlist(false);
      showToast(`Watchlist "${name}" created (disabled by default)`);
    } catch (err) {
      setWatchlistsError(err.message || 'Failed to create watchlist');
    } finally {
      setCreatingWatchlist(false);
    }
  };

  const openCreateWatchlistModal = () => {
    setNewWatchlistName('');
    setNewWatchlistExchange('US');
    setWatchlistsError(null);
    setShowCreateWatchlist(true);
  };

  const openEditWatchlistModal = (code) => {
    setEditingWatchlistCode(code);
    setEditWatchlistName(code);
    setEditWatchlistExchange(watchlistExchanges[code] || 'US');
    setWatchlistsError(null);
    setShowEditWatchlist(true);
  };

  const handleSaveEditWatchlist = async (e) => {
    e?.preventDefault?.();
    if (!editingWatchlistCode || !editWatchlistExchange) return;
    const newName = editWatchlistName.trim().toUpperCase();
    if (!newName) return;
    if (!/^[A-Z0-9_-]+$/.test(newName)) {
      setWatchlistsError('Watchlist name must contain only letters, numbers, underscore, or hyphen');
      return;
    }
    const isRename = newName !== editingWatchlistCode;
    if (isRename && watchlists && watchlists[newName] !== undefined) {
      setWatchlistsError('A watchlist with that name already exists');
      return;
    }
    setSavingEditWatchlist(true);
    setWatchlistsError(null);
    try {
      if (isRename) {
        const oldEnabled = watchlists[editingWatchlistCode];
        const [watchlistSnap, analysisSnap, reportPubSnap, reportWsSnap] = await Promise.all([
          get(ref(db, `${WATCHLIST_REF}/${editingWatchlistCode}`)),
          get(ref(db, `${ANALYSIS_REF}/${editingWatchlistCode}`)),
          get(ref(db, `${REPORT_PUBLISH_REF}/${editingWatchlistCode}`)),
          get(ref(db, `${REPORT_WORKSPACE_REF}/${editingWatchlistCode}`)),
        ]);
        const writes = [
          set(ref(db, `${WATCHLIST_REF}/${newName}`), watchlistSnap.val() ?? {}),
          update(ref(db, ANALYZE_WATCHLISTS_REF), { [newName]: oldEnabled ?? WATCHLIST_DISABLED }),
          update(ref(db, WATCHLIST_EXCHANGES_REF), { [newName]: editWatchlistExchange }),
        ];
        if (analysisSnap.exists()) writes.push(set(ref(db, `${ANALYSIS_REF}/${newName}`), analysisSnap.val()));
        if (reportPubSnap.exists()) writes.push(set(ref(db, `${REPORT_PUBLISH_REF}/${newName}`), reportPubSnap.val()));
        if (reportWsSnap.exists()) writes.push(set(ref(db, `${REPORT_WORKSPACE_REF}/${newName}`), reportWsSnap.val()));
        await Promise.all(writes);
        await Promise.all([
          set(ref(db, `${WATCHLIST_REF}/${editingWatchlistCode}`), null),
          set(ref(db, `${ANALYZE_WATCHLISTS_REF}/${editingWatchlistCode}`), null),
          set(ref(db, `${WATCHLIST_EXCHANGES_REF}/${editingWatchlistCode}`), null),
          set(ref(db, `${ANALYSIS_REF}/${editingWatchlistCode}`), null),
          set(ref(db, `${REPORT_PUBLISH_REF}/${editingWatchlistCode}`), null),
          set(ref(db, `${REPORT_WORKSPACE_REF}/${editingWatchlistCode}`), null),
        ]);
        setWatchlists((prev) => {
          const next = { ...prev, [newName]: oldEnabled ?? WATCHLIST_DISABLED };
          delete next[editingWatchlistCode];
          return next;
        });
        setWatchlistExchanges((prev) => {
          const next = { ...prev, [newName]: editWatchlistExchange };
          delete next[editingWatchlistCode];
          return next;
        });
        showToast(`Watchlist renamed from "${editingWatchlistCode}" to "${newName}"`);
      } else {
        await update(ref(db, WATCHLIST_EXCHANGES_REF), { [editingWatchlistCode]: editWatchlistExchange });
        setWatchlistExchanges((prev) => ({ ...prev, [editingWatchlistCode]: editWatchlistExchange }));
        if (watchlists[editingWatchlistCode] != null && watchlists[editingWatchlistCode] !== '' && watchlists[editingWatchlistCode] !== WATCHLIST_DISABLED) {
          await update(ref(db, ANALYZE_WATCHLISTS_REF), { [editingWatchlistCode]: editWatchlistExchange });
          setWatchlists((prev) => ({ ...prev, [editingWatchlistCode]: editWatchlistExchange }));
        }
        showToast(`Exchange for "${editingWatchlistCode}" updated`);
      }
      setShowEditWatchlist(false);
      setEditingWatchlistCode(null);
    } catch (err) {
      setWatchlistsError(err.message || 'Failed to update watchlist');
    } finally {
      setSavingEditWatchlist(false);
    }
  };

  const handleDeleteWatchlist = async (code) => {
    if (!window.confirm(`Delete watchlist "${code}" and all its stocks and analysis data? This cannot be undone.`)) {
      return;
    }
    setWatchlistsError(null);
    try {
      await Promise.all([
        set(ref(db, `${WATCHLIST_REF}/${code}`), null),
        set(ref(db, `${ANALYZE_WATCHLISTS_REF}/${code}`), null),
        set(ref(db, `${WATCHLIST_EXCHANGES_REF}/${code}`), null),
        set(ref(db, `${ANALYSIS_REF}/${code}`), null),
        set(ref(db, `${REPORT_PUBLISH_REF}/${code}`), null),
        set(ref(db, `${REPORT_WORKSPACE_REF}/${code}`), null),
      ]);
      setWatchlists((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setWatchlistExchanges((prev) => {
        const next = { ...prev };
        delete next[code];
        return next;
      });
      setWatchlistByGroup((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        delete next[code];
        return next;
      });
      showToast(`Watchlist "${code}" deleted`);
    } catch (err) {
      setWatchlistsError(err.message || 'Failed to delete watchlist');
    }
  };

  const sortedWatchlists = watchlists ? Object.keys(watchlists).sort((a, b) => a.localeCompare(b)) : [];

  return (
    <>
      <div className="admin-cards">
        <section className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">
              <HiOutlineViewGrid className="admin-card-icon" aria-hidden />
              Watchlists
            </h2>
            <span className="admin-card-count">{sortedWatchlists.length}</span>
            <button type="button" className="btn btn-primary btn-sm admin-card-header-action" onClick={openCreateWatchlistModal}>
              <HiOutlinePlus aria-hidden style={{ width: '0.9rem', height: '0.9rem' }} />
              Create watchlist
            </button>
          </div>
          <div className="admin-card-body">
            {watchlistsLoading || watchlistExchangesLoading ? (
              <p className="admin-placeholder">Loading…</p>
            ) : sortedWatchlists.length === 0 ? (
              <p className="admin-empty">No watchlists configured. Create one to get started.</p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Watchlist</th>
                      <th>Exchange</th>
                      <th>Enabled</th>
                      <th className="admin-table-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedWatchlists.map((code) => (
                      <tr key={code}>
                        <td><strong>{code}</strong></td>
                        <td>
                          {(() => {
                            const exchange = (typeof watchlists[code] === 'string' && watchlists[code] !== WATCHLIST_DISABLED)
                              ? watchlists[code]
                              : watchlistExchanges[code];
                            if (!exchange || typeof exchange !== 'string') return <span className="settings-muted">Not set</span>;
                            const opt = EXCHANGE_OPTIONS.find((o) => o.code === exchange);
                            return opt ? opt.label : exchange;
                          })()}
                        </td>
                        <td>
                          <label className="settings-switch">
                            <input type="checkbox" checked={watchlists[code] != null && watchlists[code] !== '' && watchlists[code] !== WATCHLIST_DISABLED} onChange={() => handleWatchlistToggle(code)} disabled={savingWatchlist === code} className="settings-switch-input" />
                            <span className="settings-switch-slider" />
                            <span className="settings-switch-label">{(watchlists[code] != null && watchlists[code] !== '' && watchlists[code] !== WATCHLIST_DISABLED) ? 'On' : 'Off'}</span>
                          </label>
                        </td>
                        <td className="admin-table-actions">
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => openEditWatchlistModal(code)} title={`Edit ${code}`}>
                            <HiOutlinePencil aria-hidden style={{ width: '0.85rem', height: '0.85rem' }} />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => handleDeleteWatchlist(code)}
                            title={`Delete ${code}`}
                          >
                            <HiOutlineTrash aria-hidden style={{ width: '0.85rem', height: '0.85rem' }} />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {watchlistsError && <p className="settings-row settings-row--error" role="alert">{watchlistsError}</p>}
          </div>
        </section>

        <WatchlistContent
          watchlistByGroup={watchlistByGroup}
          isAdmin={isAdmin}
          onWatchlistUpdate={setWatchlistByGroup}
          showToast={showToast}
        />
      </div>

      {showCreateWatchlist && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="create-watchlist-title" aria-describedby="create-watchlist-desc">
          <div className="modal-dialog">
            <h2 id="create-watchlist-title" className="modal-title">Create watchlist</h2>
            <p id="create-watchlist-desc" className="modal-desc">
              Create a new watchlist. It will be disabled by default until you enable it.
            </p>
            <form onSubmit={handleCreateWatchlist} className="watchlist-add-form">
              <div className="watchlist-add-field">
                <label htmlFor="admin-create-watchlist-name">Watchlist name *</label>
                <input
                  id="admin-create-watchlist-name"
                  type="text"
                  value={newWatchlistName}
                  onChange={(e) => setNewWatchlistName(e.target.value)}
                  placeholder="e.g. SNP500, FTSE100"
                  required
                  disabled={creatingWatchlist}
                  autoFocus
                  maxLength={32}
                  className="settings-input"
                />
              </div>
              <div className="watchlist-add-field">
                <label htmlFor="admin-create-watchlist-exchange">Exchange *</label>
                <select
                  id="admin-create-watchlist-exchange"
                  value={newWatchlistExchange}
                  onChange={(e) => setNewWatchlistExchange(e.target.value)}
                  disabled={creatingWatchlist}
                  className="settings-input"
                >
                  {EXCHANGE_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowCreateWatchlist(false)} disabled={creatingWatchlist}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creatingWatchlist || !newWatchlistName.trim()}>
                  {creatingWatchlist ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditWatchlist && editingWatchlistCode && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="edit-watchlist-title" aria-describedby="edit-watchlist-desc">
          <div className="modal-dialog">
            <h2 id="edit-watchlist-title" className="modal-title">Edit watchlist: {editingWatchlistCode}</h2>
            <p id="edit-watchlist-desc" className="modal-desc">
              Edit this watchlist's name or exchange. Renaming moves all data to the new key.
            </p>
            <form onSubmit={handleSaveEditWatchlist} className="watchlist-add-form">
              <div className="watchlist-add-field">
                <label htmlFor="edit-watchlist-name">Watchlist name *</label>
                <input
                  id="edit-watchlist-name"
                  type="text"
                  value={editWatchlistName}
                  onChange={(e) => setEditWatchlistName(e.target.value)}
                  required
                  disabled={savingEditWatchlist}
                  maxLength={32}
                  className="settings-input"
                />
              </div>
              <div className="watchlist-add-field">
                <label htmlFor="edit-watchlist-exchange">Exchange *</label>
                <select
                  id="edit-watchlist-exchange"
                  value={editWatchlistExchange}
                  onChange={(e) => setEditWatchlistExchange(e.target.value)}
                  disabled={savingEditWatchlist}
                  className="settings-input"
                >
                  {EXCHANGE_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => { setShowEditWatchlist(false); setEditingWatchlistCode(null); }} disabled={savingEditWatchlist}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={savingEditWatchlist || !editWatchlistName.trim()}>
                  {savingEditWatchlist ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminWatchlists;
